import { APP_REGISTRY } from '../model/appRegistry'
import { getFastNav } from '../model/registry'
import useTranslation from './useTranslation'
import useUserPermissions from './useUserPermissions'
import useScreenSize from './useScreenSize'

const LS_KEY = 'uapp_fastnav_frecency'
const HALF_LIFE_DAYS = 7
const TOP_COUNT = 5

export function recordVisit(path) {
    try {
        const data = JSON.parse(localStorage.getItem(LS_KEY) ?? '{}')
        const prev = data[path] ?? { count: 0, lastVisited: 0 }
        data[path] = { count: prev.count + 1, lastVisited: Date.now() }
        localStorage.setItem(LS_KEY, JSON.stringify(data))
    } catch { /* localStorage unavailable */ }
}

function frecencyScore(entry) {
    if (!entry || !entry.count) return 0
    const days = (Date.now() - entry.lastVisited) / 86_400_000
    return entry.count * Math.exp((-Math.LN2 / HALF_LIFE_DAYS) * days)
}

function loadFrecency() {
    try { return JSON.parse(localStorage.getItem(LS_KEY) ?? '{}') }
    catch { return {} }
}

function normalize(str) {
    return str.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
}

function assignShortcuts(items) {
    const claimed = new Set()
    return items.map(item => {
        let shortcut = null
        for (const ch of item.label.toUpperCase()) {
            if (/[A-Z]/.test(ch) && !claimed.has(ch)) {
                shortcut = ch
                claimed.add(ch)
                break
            }
        }
        return { ...item, shortcut }
    })
}

function useFastNav(query) {
    const { t } = useTranslation()
    const { isOwner, apps } = useUserPermissions()
    const { isMobile } = useScreenSize()

    const permitted = getFastNav().filter(item => {
        if (item.desktopOnly && isMobile) return false
        if (!item.app || item.app === 'home') return true
        const reg = APP_REGISTRY[item.app]
        if (!reg) return true
        if (reg.ownerOnly) return isOwner
        return isOwner || apps.includes(item.app)
    })

    const withLabels = permitted.map(item => ({
        ...item,
        label: item.app && item.app !== 'home' && APP_REGISTRY[item.app]
            ? `${t(item.title)} — ${t(APP_REGISTRY[item.app].title)}`
            : t(item.title),
    }))

    if (query) {
        const q = normalize(query)
        const filtered = withLabels.filter(item => normalize(item.label).includes(q))
        return { topItems: [], allItems: filtered, hasTop: false }
    }

    const frecency = loadFrecency()
    const scored = withLabels.map(item => ({
        ...item,
        score: frecencyScore(frecency[item.path]),
    }))

    const topItems = scored
        .filter(i => i.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, TOP_COUNT)

    const topPaths = new Set(topItems.map(i => i.path))
    const allItems = withLabels
        .filter(i => !topPaths.has(i.path))
        .sort((a, b) => a.label.localeCompare(b.label))

    const withShortcuts = assignShortcuts([...topItems, ...allItems])
    const splitAt = topItems.length

    return {
        topItems:  withShortcuts.slice(0, splitAt),
        allItems:  withShortcuts.slice(splitAt),
        hasTop:    topItems.length > 0,
    }
}

export default useFastNav
